/**
 * The editor's query-string vocabulary: three constants and the one rule
 * that decides what the address bar carries.
 */

/** The query key for the editor tab; absent means the editor tab. */
export const TAB_QUERY_KEY = 'tab';
/**
 * LEGACY. The query key that used to name the previewed project, back when
 * the path named an arbitrary member of it.
 *
 * The project is in the PATH now. Nothing writes this; the URL writer strips
 * it so a link made under the old scheme rewrites itself, and the route saga
 * still reads it so that link resolves on the way through. Both can go once
 * no such link can plausibly exist.
 */
export const COMBINED_QUERY_KEY = 'combined';
/** The only tab worth writing down — `editor` is the default. */
export const REDACTED_TAB_QUERY_VALUE = 'redacted';

/**
 * The query the editor should be showing, given the one it has.
 *
 * ONE RULE, TWO WRITERS. `editorUrlState` runs it when the TAB changes, and
 * the Documents panel's switch runs it when the DOCUMENT changes. The switch
 * used to build its query from scratch — `{tab}` on the redacted tab,
 * nothing otherwise — so everything else the reviewer arrived with was
 * dropped the moment they picked another file. `?mode=review` and
 * `?mode=viewOnly` survived exactly one document and then silently became a
 * full editor, which is a permission the link never granted.
 *
 * CARRIED: whatever came in, untouched — this module does not need to know
 * what a param means in order to keep it.
 * DROPPED: `tab`, restated below from the state rather than inherited, and
 * `combined`, the legacy project pointer the path replaced.
 *
 * `pathIsProject` suppresses the tab: a project URL IS the merged output,
 * and the route saga forces that tab from the path alone.
 */
export const editorQuery = (
  current: Record<string, string | undefined> | undefined,
  activeTab: string,
  pathIsProject: boolean
): Record<string, string> => {
  const next: Record<string, string> = {};
  for (const [key, value] of Object.entries(current ?? {})) {
    if (
      value !== undefined &&
      key !== TAB_QUERY_KEY &&
      key !== COMBINED_QUERY_KEY
    ) {
      next[key] = value;
    }
  }
  if (activeTab === 'redacted-file' && !pathIsProject) {
    next[TAB_QUERY_KEY] = REDACTED_TAB_QUERY_VALUE;
  }
  return next;
};
